import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

import { PolicyTypes } from '../models/PasswordPoliciesDtoModel';
import { ByPatternPolicy, ByValuePolicy, ByValueRules, PasswordPolicy } from '../models/PasswordPoliciesInternalModel';

/**
 * Prüft, ob ein Passwort den Anforderungen einer ByPattern-Regel entspricht.
 *
 * @param rule Die ByPattern-Regel, die geprüft werden soll. Das `pattern` ist hier
 * bereits ein RegExp-Objekt, da es vom Service konvertiert wurde.
 * @param password Das zu prüfende Passwort als String.
 * @returns `true`, wenn das Passwort dem Pattern entspricht, sonst `false`.
 */
export const isByPatternRuleMatched = (rule: ByPatternPolicy, password: string): boolean => {
  const value = password || '';
  return rule.pattern.test(value);
};

/**
 * Prüft, ob ein Passwort den Anforderungen einer ByValue-Regel entspricht.
 *
 * Diese Methode validiert Regeln wie Mindest- oder Maximallänge.
 *
 * @param rule Die ByValue-Regel, die geprüft werden soll.
 * @param password Das zu prüfende Passwort als String.
 * @returns `true`, wenn das Passwort der Regel (z.B. Mindestlänge) entspricht, sonst `false`.
 */
export const isByValueRuleMatched = (rule: ByValuePolicy, password: string): boolean => {
  const value = password || '';

  if (rule.name === ByValueRules.MinimumLength) {
    return value.length >= rule.value;
  }

  if (rule.name === ByValueRules.MaximumLength) {
    return value.length <= rule.value;
  }

  return false;
};

/**
 * Prüft, ob eine gegebene Passwortregel durch den angegebenen Passwortwert erfüllt ist.
 *
 * @param rule Die zu prüfende Passwortregel
 * @param password Das zu validierende Passwort
 * @returns `true`, wenn die Regel erfüllt ist, sonst `false`
 */
export const isRuleMatched = (rule: PasswordPolicy, password: string): boolean => {
  switch (rule.type) {
    case PolicyTypes.ByPattern:
      return isByPatternRuleMatched(rule as ByPatternPolicy, password);

    case PolicyTypes.ByValue:
      return isByValueRuleMatched(rule as ByValuePolicy, password);

    default:
      return false;
  }
};

/**
 * Generiert einen Übersetzungsschlüssel für eine gegebene Passwortregel.
 *
 * Die Funktion nutzt den `ngx-translate-extract-marker`, um den String für
 * die automatische Extraktion der Übersetzungen zu markieren.
 *
 * @param policyName Der Name der Regel (z.B. 'minimumLength').
 * @returns Den generierten Übersetzungsschlüssel als String.
 */
export const getPasswordPolicyTranslationKey = (policyName: string): string => {
  return _(`passwort_meter.rule_description.${policyName}`);
};

/**
 * Eine Typ-Guard-Funktion, die prüft, ob ein `PasswordPolicy`-Objekt vom Typ `ByValuePolicy` ist.
 *
 * @param policy Das zu prüfende Policy-Objekt.
 * @returns `true`, wenn das Objekt eine `ByValuePolicy` ist, andernfalls `false`.
 */
export const isByValuePolicy = (policy: PasswordPolicy): policy is ByValuePolicy =>
  policy.type === PolicyTypes.ByValue && 'value' in policy && typeof policy.value === 'number';
